import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { ApiErrorBody } from "./types";
import { useDevAuthStore } from "../../stores/devAuth";
import { useAuthStore } from "../../stores/auth";

/**
 * console-server now implements real authentication (HttpOnly session
 * cookies + CSRF double-submit header -- see
 * console/cmd/console-server/{cookies,csrf}.go and
 * docs/decisions/ADR-002's follow-on). This client was already
 * structured to match atf-ee's shipping axios pattern
 * (withCredentials cookie session + CSRF double-submit header) from day
 * one per ADR-002, so wiring real auth required no client rewrite --
 * only the response-interceptor 401 handling below actually changed.
 *
 * The dev-only org/user headers (X-Dev-Organization-Id/X-Dev-User-Id)
 * are still injected below for local development against a
 * JWT_SECRET-less console-server (see DevAuthPanel.tsx) -- the backend
 * does not treat them as authorization once real auth middleware is
 * wired in; they are vestigial once a real session cookie is present,
 * kept only so the dev escape hatch continues to work when auth
 * middleware is bypassed (e.g. a route console-server's own public
 * allowlist already exempts, or during local dev before logging in).
 */

const API_URL = import.meta.env.VITE_API_URL as string | undefined;

export const apiClient = axios.create({
  baseURL: API_URL ? `${API_URL}/api/v1` : "/api/v1",
  timeout: 15000,
  withCredentials: true,
});

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// Request interceptor: JSON content-type + CSRF double-submit header on
// mutating requests, matching atf-ee's client.ts pattern. Also injects the
// dev-mode acting-as headers so pages can be exercised against the real
// backend without a real login system (see stores/devAuth.ts).
apiClient.interceptors.request.use((config) => {
  if (!(config.data instanceof FormData) && !config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }

  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    // __Host- prefixed name when the server runs with Secure cookies; plain
    // name for local HTTP dev and for sessions that predate the prefix.
    const csrfToken = readCookie("__Host-csrf_token") ?? readCookie("csrf_token");
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }

  // DEV-ONLY: until real cookie-session auth exists on console's backend,
  // stamp the dev auth panel's chosen org/user context onto every request
  // as headers. The backend does not currently read these headers -- they
  // exist so the client code (and any future backend middleware) has a
  // stable, obvious place to look. Real requests still carry the
  // organization_id explicitly in path/body per the proto contract; this is
  // purely a convenience so call sites can default to "the org I'm acting
  // as" without threading it through every component.
  const devAuth = useDevAuthStore.getState();
  if (devAuth.organizationId) {
    config.headers["X-Dev-Organization-Id"] = devAuth.organizationId;
  }
  if (devAuth.userId) {
    config.headers["X-Dev-User-Id"] = devAuth.userId;
  }

  return config as InternalAxiosRequestConfig;
});

// Response interceptor: console's response messages are NOT wrapped in a
// generic {success, data} envelope the way atf-ee's are -- each proto
// response message declares its own `success`/`message` fields alongside
// its payload (see lib/api/types.ts header comment). So there is nothing to
// unwrap here; we pass response.data straight through as the typed
// response shape.
//
// 401 handling: a single-flight refresh-and-retry (atf-ee's pattern) is
// NOT implemented here -- console's confirmed scope keeps refresh
// simple (no rotation, no silent-retry queue). Instead, a 401 clears the
// local auth store so the UI immediately reflects "you are logged out"
// and route guards can redirect to /login. The one exception is the
// refresh endpoint itself, which callers invoke explicitly (e.g. on app
// bootstrap) rather than this interceptor doing it implicitly, to avoid
// infinite-retry loops if refresh itself ever 401s.
let loggedOutWarningShown = false;
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/")) {
      useAuthStore.getState().clear();
      if (!loggedOutWarningShown) {
        loggedOutWarningShown = true;
        // eslint-disable-next-line no-console
        console.warn("[console-web] session expired or invalid (401) -- please log in again.");
      }
    }
    return Promise.reject(error);
  },
);

/** Extract a human-readable message from a failed API call, regardless of shape. */
export function apiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.message) return body.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unexpected error";
}
